import { Button, Flex } from '@cad-arc';
import React from 'react';

export type RecommendationPeekChipState = 'loading' | 'ready';

/**
 * The floating recommendation peek chip — a real Arc Button (design-system dimensions for `size="md"`
 * plus a11y for free) that mirrors the Recommended section's status when that section is scrolled out
 * of view. The icon is STATIC (the spinner lives only on the inline gathering row), so there is no
 * Electron emotion-keyframe risk here.
 *
 * Rendered inside the (flag-on) `position:relative` DrawerBody. The full-width wrapper centers the
 * button so it hugs its content (a bare `left:50%` would cap it at half the panel and wrap the label);
 * `pointerEvents:none` on the wrapper lets clicks fall through to the list row beneath, and the button
 * re-enables them on itself.
 */
export function RecommendationPeekChip({
    state,
    onClick,
}: {
    state: RecommendationPeekChipState;
    onClick: () => void;
}): React.ReactElement {
    const isReady = state === 'ready';
    return (
        <Flex
            position="absolute"
            top="20px"
            left="0"
            right="0"
            justifyContent="center"
            zIndex={3}
            pointerEvents="none"
            // Shift down by half the chip's own height so it rides centered on the top line of the
            // first unit card (with a group header above, it straddles that card's top edge).
            transform="translateY(50%)"
        >
            <Button
                variant="solid"
                size="md"
                // Figma: loading = colorVariant "black"; ready = default (brand #305CE0).
                colorVariant={isReady ? undefined : 'black'}
                leftIcon={isReady ? 'ArrowUp' : 'Sync'}
                onClick={onClick}
                pointerEvents="auto"
                // Pill ends. The Arc Button recipe overrides the `borderRadius` *prop* with its own md
                // radius (a rounded rectangle), so we force the radius via inline `style`, which wins
                // over the emotion class.
                style={{ borderRadius: '9999px' }}
                data-testid={isReady ? 'recommendations-ready' : 'recommendations-loading'}
            >
                {isReady ? 'Recommendations Ready' : 'Recommendations loading'}
            </Button>
        </Flex>
    );
}
